import React, { useState, useRef } from 'react';
import { Card, Row, Col, Button, Form, InputGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Envelope, Telephone, Camera, PersonCircle } from 'react-bootstrap-icons';

const AccountInfo = () => {
    const { userData } = useSelector((state) => state.auth);
    const employee = userData?.employee || {};

    const [profileData, setProfileData] = useState({
        firstName: employee.emp_name?.split(' ')[0] || '',
        lastName: employee.emp_name?.split(' ').slice(1).join(' ') || '',
        email: employee.email || '',
        phone: employee.phone || '',
        bio: employee.bio || '',
        image: employee.profile_picture || null
    });

    const fileInputRef = useRef(null);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileData({ ...profileData, image: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setProfileData({ ...profileData, image: null });
    };

    const initials = (profileData.firstName?.charAt(0) || employee.emp_name?.charAt(0) || 'U').toUpperCase();

    return (
        <Card className="border-0 shadow-sm rounded-3">
            <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-4 gap-2">
                    <div className="bg-primary-subtle p-2 rounded-2 d-flex align-items-center justify-content-center">
                        <PersonCircle className="text-primary" size={18} />
                    </div>
                    <h6 className="fw-bold mb-0 text-primary fs-10">Personal Information</h6>
                </div>

                {/* Profile Photo Section */}
                <div className="d-flex align-items-center mb-4">
                    <div className="position-relative profile-image-container me-3">
                        <div
                            className="rounded-3 overflow-hidden position-relative"
                            style={{ width: '70px', height: '70px', cursor: 'pointer' }}
                            onClick={() => fileInputRef.current.click()}
                        >
                            {profileData.image ? (
                                <img
                                    src={profileData.image}
                                    alt="Profile"
                                    className="w-100 h-100 object-fit-cover shadow-sm"
                                />
                            ) : (
                                <div className="w-100 h-100 bg-info d-flex align-items-center justify-content-center text-white fw-bold fs-7 shadow-sm">
                                    {initials}
                                </div>
                            )}
                            <div className="camera-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center opacity-0 transition-base">
                                <Camera className="text-white" size={20} />
                            </div>
                        </div>
                        <input
                            type="file"
                            hidden
                            ref={fileInputRef}
                            onChange={handleImageChange}
                            accept="image/*"
                        />
                    </div>
                    <div>
                        <h6 className="fw-bold mb-1 text-900 fs-11">Profile Photo</h6>
                        <p className="text-500 fs-11 mb-2">Upload a professional headshot. Max 5MB.</p>
                        <div className="d-flex gap-2 align-items-center">
                            <Button
                                variant="primary-subtle"
                                className="text-primary btn-xs px-2 fw-medium border-0 fs-11"
                                style={{ backgroundColor: 'rgba(var(--falcon-primary-rgb), 0.1)' }}
                                onClick={() => fileInputRef.current.click()}
                            >
                                Change Photo
                            </Button>
                            <Button
                                variant="link"
                                className="text-500 text-decoration-none btn-xs fw-medium p-0 ms-1 fs-11 remove-btn"
                                onClick={handleRemoveImage}
                            >
                                Remove
                            </Button>
                        </div>
                    </div>
                </div>

                <Form>
                    <Row className="g-3 mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-700 fw-medium fs-11 mb-1">First Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    defaultValue={profileData.firstName}
                                    placeholder="Enter first name"
                                    className=" border-0 py-2 fs-10"
                                    style={{ border: '1px solid #edf2f9 !important' }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-700 fw-medium fs-11 mb-1">Last Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    defaultValue={profileData.lastName}
                                    placeholder="Enter last name"
                                    className=" border-0 py-2 fs-10"
                                    style={{ border: '1px solid #edf2f9 !important' }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="g-3 mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-700 fw-medium fs-11 mb-1">Email Address</Form.Label>
                                <div className="input-group  rounded-3 overflow-hidden border-0" style={{ border: '1px solid #edf2f9 !important' }}>
                                    <span className="input-group-text bg-transparent border-0 pe-0 text-500">
                                        <Envelope size={12} />
                                    </span>
                                    <Form.Control
                                        type="email"
                                        defaultValue={profileData.email}
                                        placeholder="Enter email address"
                                        className="bg-transparent border-0 py-2 fs-10 shadow-none"
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-700 fw-medium fs-11 mb-1">Phone Number</Form.Label>
                                <div className="input-group  rounded-3 overflow-hidden border-0" style={{ border: '1px solid #edf2f9 !important' }}>
                                    <span className="input-group-text bg-transparent border-0 pe-0 text-500">
                                        <Telephone size={12} />
                                    </span>
                                    <Form.Control
                                        type="text"
                                        defaultValue={profileData.phone}
                                        placeholder="Enter phone number"
                                        className="bg-transparent border-0 py-2 fs-10 shadow-none"
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-0">
                        <Form.Label className="text-700 fw-medium fs-11 mb-1">About Me / Bio</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            defaultValue={profileData.bio}
                            placeholder="Tell us about yourself..."
                            className=" border-0 py-2 fs-10 resize-none px-3"
                            style={{ border: '1px solid #edf2f9 !important' }}
                        />
                    </Form.Group>
                </Form>
            </Card.Body>

            <style dangerouslySetInnerHTML={{
                __html: `
                .profile-image-container:hover .camera-overlay {
                    opacity: 1 !important;
                    background-color: rgba(0, 0, 0, 0.4);
                }
                .transition-base {
                    transition: all 0.2s ease-in-out;
                }
                .btn-xs {
                    padding: 0.125rem 0.5rem;
                    line-height: 1.5;
                    border-radius: 0.2rem;
                }
                .remove-btn:hover {
                    color: var(--falcon-danger) !important;
                }
                .fs-11 { font-size: 0.75rem !important; }
                .fs-10 { font-size: 0.8125rem !important; }
                .fs-7 { font-size: 1.5rem !important; }
            `}} />
        </Card>
    );
};

export default AccountInfo;
